import React from 'react';
import { ReactSortable } from 'react-sortablejs';
import {
  Box,
  Chip,
  Collapse,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  useCampaignQuestions,
  useFetchCampaignSteps,
  useReferenceObjectAdmin,
} from '../../../../hooks/campaign-hooks';
import {
  Campaign,
  CampaignOutcome,
  CampaignQuestion,
  OUTCOME_CONTEXTS,
  OutcomeContext,
} from '../../../../support/campaigns';
import { GhostButton, SectionHeader, builderStyles } from './primitives';
import { byOrder, nextOrderOf, signature } from './builderShared';
import { buildReferenceUsage, questionUsage, WhereUsed } from './referenceUsage';

/**
 * Admin editor for a campaign's prioritized outcomes (`CampaignOutcome`) — what the motion is meant to
 * achieve, which steers the campaign's AI framing and readouts. The question pool that draws on these
 * lives in its own reference-object tab (`QuestionPoolEditor`).
 *
 * Outcomes are drag-orderable — the top outcome carries the most weight, so the drag rank IS the
 * per-campaign priority the author sets — and split by `context` (customer-facing vs internal/Cisco) so
 * the two never mix in reporting or generation. Rows are not edited inline: each shows a read-only
 * summary and an Edit control that expands its config plus a "where it's used" section. An outcome is
 * used indirectly — by the pool questions that explore it — so its usage is the questions that cite it
 * and the steps those questions surface in.
 *
 * Persistence is the builder's usual full-record PUT: rows create on "Add", text flushes on blur,
 * select/toggle changes flush on change; a local mirror keeps typing responsive between refetches.
 */
export function OutcomesEditor(props: { campaign: Campaign | null; canWrite: boolean }): JSX.Element {
  const { campaign, canWrite } = props;

  // Global (library) mode = no campaign: manage every global outcome directly (create/edit/delete).
  // Campaign mode = a campaign references a subset: `admin.linked` is that subset, `admin.unlinked`
  // backs the "attach existing" picker, and `createLinked`/`detach` create-and-link / detach without
  // deleting the (possibly shared) global outcome.
  const global = campaign == null;
  const admin = useReferenceObjectAdmin<CampaignOutcome>('outcome', campaign?.id);
  const outcomeRows = global ? admin.all : admin.linked;
  const isLoading = admin.isLoading;
  const { data: questionRows = [] } = useCampaignQuestions(campaign?.id);
  const { data: steps = [] } = useFetchCampaignSteps(campaign?.id);
  const outcomeMut = admin;

  const [attachAnchor, setAttachAnchor] = React.useState<null | HTMLElement>(null);
  const [outcomes, setOutcomes] = React.useState<CampaignOutcome[]>([]);
  const outcomesSig = signature(outcomeRows);
  React.useEffect(() => {
    setOutcomes(byOrder(outcomeRows));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcomesSig]);

  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());
  const toggleExpanded = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const stepsSig = signature(steps);
  const usage = React.useMemo(
    () => buildReferenceUsage(steps),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepsSig],
  );
  const questions = React.useMemo(() => byOrder(questionRows), [questionRows]);

  // An outcome's usage: the pool questions that explore it, and the union of steps those questions
  // surface in (a pool question links to its content block via `metadata.source`).
  const outcomeUsage = (outcomeId: number) => {
    const exploring = questions.filter((q: CampaignQuestion) => q.outcome === outcomeId);
    const stepSet: typeof steps = [];
    for (const q of exploring) {
      const source = (q.metadata as { source?: { category?: string; key?: string } } | null)?.source;
      for (const s of questionUsage(usage, source)) if (!stepSet.includes(s)) stepSet.push(s);
    }
    return { questionCount: exploring.length, steps: stepSet };
  };

  const contextLabel = (c: OutcomeContext) =>
    OUTCOME_CONTEXTS.find((o) => o.value === c)?.label ?? c;

  const patchLocal = (id: number, next: Partial<CampaignOutcome>) =>
    setOutcomes((prev) => prev.map((o) => (o.id === id ? { ...o, ...next } : o)));

  const save = (row: CampaignOutcome, next: Partial<CampaignOutcome>) => {
    patchLocal(row.id, next);
    outcomeMut.update.mutate({ ...row, ...next, id: row.id });
  };

  const addOutcome = () => {
    const fields = {
      title: '',
      description: '',
      context: 'customer' as OutcomeContext,
      order: nextOrderOf(outcomes),
      hidden: false,
      metadata: {},
    };
    // Global mode creates a bare global outcome; campaign mode creates-and-links it to the campaign.
    if (global) void admin.create(fields);
    else void admin.createLinked(fields);
  };

  const attachOutcome = (id: number) => admin.attachExisting(id);

  // Campaign mode: "Remove" detaches this campaign's reference; the global outcome (which another
  // campaign may reference) is left intact. Global mode: delete the global outcome everywhere (with
  // confirmation, since it is removed from every campaign that references it).
  const removeOutcome = (row: CampaignOutcome) => {
    if (global) {
      const ok = window.confirm(
        `Delete “${row.title.trim() || 'this outcome'}” from the global library? It will be removed from every campaign that references it.`,
      );
      if (!ok) return;
      setOutcomes((prev) => prev.filter((o) => o.id !== row.id));
      admin.removeGlobal(row.id);
      return;
    }
    setOutcomes((prev) => prev.filter((o) => o.id !== row.id));
    admin.detach(row.id);
  };

  // Persist a new drag order: PUT every outcome whose position changed (full record + new `order`).
  const reorderOutcomes = (nextList: CampaignOutcome[]) => {
    setOutcomes(nextList);
    nextList.forEach((row, index) => {
      if ((row.order ?? 0) !== index) {
        outcomeMut.update.mutate({ ...row, order: index, id: row.id });
      }
    });
  };

  return (
    <div className={builderStyles.contentCard}>
      <SectionHeader
        compact
        title="Intended outcomes"
        subtitle="What this campaign is meant to achieve. Drag to prioritise — the top outcome carries the most weight in AI framing and readouts. Customer-facing and internal are kept separate so reporting never mixes them."
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          {outcomes.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No outcomes yet. Add the results this motion is meant to drive.
            </Typography>
          )}

          <ReactSortable
            list={outcomes.map((o) => ({ ...o }))}
            setList={(next) => canWrite && reorderOutcomes(next as CampaignOutcome[])}
            handle=".cb-outcome-drag-handle"
            animation={150}
            disabled={!canWrite}
          >
            {outcomes.map((row) => {
              const isOpen = expanded.has(row.id);
              const used = outcomeUsage(row.id);
              return (
                <Box
                  key={row.id}
                  sx={{
                    border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 1,
                    p: 1,
                    mb: 1,
                    opacity: row.hidden ? 0.55 : 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box
                      className="cb-outcome-drag-handle"
                      sx={{ cursor: canWrite ? 'grab' : 'default', color: 'text.disabled', pt: 0.25, display: 'flex' }}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.title.trim() || <em>(untitled outcome)</em>}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }} useFlexGap flexWrap="wrap">
                        <Chip size="small" variant="outlined" label={contextLabel(row.context)} />
                        {global ? (
                          <Typography variant="caption" color="text.secondary">
                            · used by {admin.usageCount(row.id)} campaign
                            {admin.usageCount(row.id) === 1 ? '' : 's'}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            · {used.questionCount} question{used.questionCount === 1 ? '' : 's'} explore this
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    {canWrite && (
                      <Stack direction="row">
                        <Tooltip title={isOpen ? 'Close' : 'Edit'}>
                          <IconButton size="small" onClick={() => toggleExpanded(row.id)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={row.hidden ? 'Hidden from AI — show' : 'Exclude from AI context'}>
                          <IconButton size="small" onClick={() => save(row, { hidden: !row.hidden })}>
                            {row.hidden ? (
                              <VisibilityOffIcon fontSize="small" />
                            ) : (
                              <VisibilityIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={global ? 'Delete outcome (removes it from all campaigns)' : 'Remove outcome'}>
                          <IconButton size="small" onClick={() => removeOutcome(row)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>

                  <Collapse in={isOpen} unmountOnExit>
                    <Stack spacing={1} sx={{ mt: 1.25 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                          label="Outcome"
                          size="small"
                          fullWidth
                          value={row.title}
                          disabled={!canWrite}
                          onChange={(e) => patchLocal(row.id, { title: e.target.value })}
                          onBlur={() => save(row, { title: row.title })}
                        />
                        <TextField
                          select
                          label="Context"
                          size="small"
                          value={row.context}
                          disabled={!canWrite}
                          onChange={(e) => save(row, { context: e.target.value as OutcomeContext })}
                          sx={{ minWidth: 170 }}
                        >
                          {OUTCOME_CONTEXTS.map((c) => (
                            <MenuItem key={c.value} value={c.value}>
                              {c.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                      <TextField
                        label="Description (optional)"
                        size="small"
                        fullWidth
                        multiline
                        value={row.description}
                        disabled={!canWrite}
                        onChange={(e) => patchLocal(row.id, { description: e.target.value })}
                        onBlur={() => save(row, { description: row.description })}
                      />
                      {!global && <WhereUsed steps={used.steps} noun="outcome" />}
                    </Stack>
                  </Collapse>
                </Box>
              );
            })}
          </ReactSortable>

          {canWrite && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <GhostButton onClick={addOutcome} disabled={outcomeMut.isSaving}>
                + Add outcome
              </GhostButton>
              {/* "Attach existing" only makes sense inside a campaign; the global library shows all. */}
              {!global && (
                <>
                  <GhostButton
                    onClick={(e) => setAttachAnchor(e.currentTarget)}
                    disabled={outcomeMut.isSaving || admin.unlinked.length === 0}
                  >
                    Add existing
                  </GhostButton>
                  <Menu
                    anchorEl={attachAnchor}
                    open={Boolean(attachAnchor)}
                    onClose={() => setAttachAnchor(null)}
                  >
                    {admin.unlinked.map((o) => (
                      <MenuItem
                        key={o.id}
                        onClick={() => {
                          attachOutcome(o.id);
                          setAttachAnchor(null);
                        }}
                      >
                        {o.title.trim() || `(untitled outcome #${o.id})`}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Stack>
          )}
        </>
      )}
    </div>
  );
}

export default OutcomesEditor;
